import type {
  CreateEmployeeDto,
  DepartmentDto,
  EmployeeDetailDto,
  EmployeeListItemDto,
  EmployeeMovementDto,
  OrgChartNode,
  Paginated,
  PositionDto,
  SalaryGradeDto,
  UpdateEmployeeDto,
  UpdateOwnProfileDto,
} from '@hrms/shared';
import { del, get, patch, post } from '@/lib/api-client';

export interface Option {
  id: string;
  code: string;
  name: string;
}

/** What a person-picker needs, and nothing more. */
export interface EmployeeOption {
  id: string;
  employeeNo: string;
  fullName: string;
  departmentName: string;
}

export interface PositionOption {
  id: string;
  code: string;
  title: string;
  departmentId: string;
}

export const employeesApi = {
  list: (params: Record<string, unknown>) =>
    get<Paginated<EmployeeListItemDto>>('/employees', { params }),

  /** Unpaginated names for "pick a person" selects. */
  options: () => get<EmployeeOption[]>('/employees/options'),

  detail: (id: string) => get<EmployeeDetailDto>(`/employees/${id}`),

  me: () => get<EmployeeDetailDto>('/employees/me'),

  movements: (id: string) => get<EmployeeMovementDto[]>(`/employees/${id}/movements`),

  nextEmployeeNo: () => get<{ employeeNo: string }>('/employees/next-employee-no'),

  create: (body: CreateEmployeeDto) => post<EmployeeDetailDto>('/employees', body),

  update: (id: string, body: UpdateEmployeeDto) =>
    patch<EmployeeDetailDto>(`/employees/${id}`, body),

  updateMe: (body: UpdateOwnProfileDto) => patch<EmployeeDetailDto>('/employees/me', body),

  remove: (id: string) => del<void>(`/employees/${id}`),

  restore: (id: string) => post<EmployeeDetailDto>(`/employees/${id}/restore`),

  orgChart: (rootId?: string) =>
    get<OrgChartNode[]>('/employees/org-chart', { params: rootId ? { rootId } : {} }),
};

export const departmentsApi = {
  list: (params: Record<string, unknown>) =>
    get<Paginated<DepartmentDto>>('/departments', { params }),
  options: () => get<Option[]>('/departments/options'),
  detail: (id: string) => get<DepartmentDto>(`/departments/${id}`),
  create: (body: unknown) => post<DepartmentDto>('/departments', body),
  update: (id: string, body: unknown) => patch<DepartmentDto>(`/departments/${id}`, body),
  remove: (id: string) => del<void>(`/departments/${id}`),
};

export const positionsApi = {
  list: (params: Record<string, unknown>) => get<Paginated<PositionDto>>('/positions', { params }),
  options: (departmentId?: string) =>
    get<PositionOption[]>('/positions/options', {
      params: departmentId ? { departmentId } : {},
    }),
  detail: (id: string) => get<PositionDto>(`/positions/${id}`),
  create: (body: unknown) => post<PositionDto>('/positions', body),
  update: (id: string, body: unknown) => patch<PositionDto>(`/positions/${id}`, body),
  remove: (id: string) => del<void>(`/positions/${id}`),
};

export const salaryGradesApi = {
  list: (params: Record<string, unknown>) =>
    get<Paginated<SalaryGradeDto>>('/salary-grades', { params }),
  options: () => get<Option[]>('/salary-grades/options'),
  create: (body: unknown) => post<SalaryGradeDto>('/salary-grades', body),
  update: (id: string, body: unknown) => patch<SalaryGradeDto>(`/salary-grades/${id}`, body),
  remove: (id: string) => del<void>(`/salary-grades/${id}`),
};

/**
 * Query keys.
 *
 * Built as arrays so a prefix invalidates everything beneath it:
 * `invalidateQueries({ queryKey: employeeKeys.all })` clears every list, page
 * and detail at once. Hand-written string keys drift and leave stale rows on
 * screen after a save.
 */
export const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...employeeKeys.lists(), params] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
  movements: (id: string) => [...employeeKeys.all, 'movements', id] as const,
  me: () => [...employeeKeys.all, 'me'] as const,
  orgChart: (rootId?: string) => [...employeeKeys.all, 'org-chart', rootId ?? 'root'] as const,
  options: () => [...employeeKeys.all, 'options'] as const,
};

export const organisationKeys = {
  departments: ['departments'] as const,
  departmentList: (params: Record<string, unknown>) => ['departments', 'list', params] as const,
  departmentOptions: () => ['departments', 'options'] as const,
  positions: ['positions'] as const,
  positionList: (params: Record<string, unknown>) => ['positions', 'list', params] as const,
  positionOptions: (departmentId?: string) =>
    ['positions', 'options', departmentId ?? 'all'] as const,
  salaryGrades: ['salary-grades'] as const,
  salaryGradeList: (params: Record<string, unknown>) => ['salary-grades', 'list', params] as const,
  salaryGradeOptions: () => ['salary-grades', 'options'] as const,
};
